import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

import { DashboardShell } from "@/components/DashboardShell";
import { ListingForm } from "@/components/ListingForm";
import { PARTNER_NAV } from "@/lib/nav";
import { SERVICES, SERVICE_LABEL, useMyServices, type ServiceType } from "@/lib/services";
import { RENEW_MESSAGE, useMySubscriptions } from "@/lib/subscriptions";

export const Route = createFileRoute("/_authenticated/partner/listings/new")({
  head: () => ({
    meta: [
      { title: "Create a listing · Landoo" },
      {
        name: "description",
        content: "Create a Sale, Rent, Lease or Parking listing under an active Landoo service.",
      },
    ],
  }),
  component: NewListingPage,
});

function NewListingPage() {
  const { canList, isLoading } = useMyServices();
  const { isExpired } = useMySubscriptions();
  const [service, setService] = useState<ServiceType | null>(null);

  // An expired subscription disables NEW listings; approved listings stay live.
  const allowed = (t: ServiceType) => canList(t) && !isExpired(t);
  const available = SERVICES.filter((s) => allowed(s.value));
  const expired = SERVICES.filter((s) => canList(s.value) && isExpired(s.value));

  if (!isLoading && available.length === 0) {
    return (
      <DashboardShell title="Create a listing" role="Partner" items={PARTNER_NAV}>
        <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          {expired.length > 0 ? (
            <>
              <span className="font-semibold text-brown-dark">Renew Subscription</span>
              <p className="mt-1">
                {RENEW_MESSAGE} Expired: {expired.map((s) => SERVICE_LABEL[s.value]).join(", ")}.
              </p>
              <div className="mt-3">
                <Link
                  to="/partner/subscription"
                  className="inline-flex rounded-full bg-brand px-4 py-2 text-xs font-semibold text-brown-dark shadow-soft"
                >
                  Renew Subscription
                </Link>
              </div>
            </>
          ) : (
            <>
              This service is not activated.
              <p className="mt-1">
                Each service needs its own registration, verification, agreement, subscription and
                admin approval before you can publish listings.
              </p>
              <div className="mt-3">
                <Link
                  to="/partner/services/add"
                  className="inline-flex rounded-full bg-brand px-4 py-2 text-xs font-semibold text-brown-dark shadow-soft"
                >
                  Activate a service
                </Link>
              </div>
            </>
          )}
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title="Create a listing" role="Partner" items={PARTNER_NAV}>
      {!service ? (
        <div className="mx-auto max-w-2xl space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Choose one of your active services
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {SERVICES.map((s) => {
              const active = allowed(s.value);
              const lapsed = canList(s.value) && isExpired(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  disabled={!active}
                  onClick={() => setService(s.value)}
                  className={`rounded-2xl border p-4 text-left shadow-soft transition ${
                    active
                      ? "border-border bg-card hover:border-brand"
                      : "border-dashed border-border bg-muted/40 opacity-70"
                  }`}
                >
                  <h3 className="text-sm font-semibold text-brown-dark">{s.cardTitle}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {active
                      ? s.blurb
                      : lapsed
                        ? "Subscription expired — Renew Subscription to add listings."
                        : "This service is not activated."}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <>
          <div className="mx-auto mb-3 max-w-2xl">
            <button
              type="button"
              onClick={() => setService(null)}
              className="rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-brown-dark"
            >
              ← Change service
            </button>
          </div>
          <ListingForm service={service} />
        </>
      )}
    </DashboardShell>
  );
}
